/**
 * WebSocket Connection Manager
 *
 * BRO-564: Monitors WebSocket connection health and handles automatic reconnection
 * when the browser tab becomes visible after sleep/idle periods.
 *
 * BRO-681: Added periodic health check to detect silent connection death while
 * the tab is open in the foreground (common for operators monitoring chat).
 *
 * Responsibilities:
 * 1. Monitor Pusher connection state
 * 2. Listen for visibility change and focus events
 * 3. Detect stale connections via periodic health checks
 * 4. Trigger reconnection with exponential backoff
 * 5. Emit events for UI components to react to connection state
 */

class WebSocketConnectionManager {
    constructor() {
        this.isConnected = false;
        this.lastActivity = Date.now();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10; // BRO-681: Increased from 5 for long-running sessions
        this.baseReconnectDelay = 1000; // 1 second
        this.maxReconnectDelay = 16000; // 16 seconds
        this.staleThreshold = 60000; // 1 minute - connection considered stale if no activity
        this.healthCheckInterval = null;
        this.healthCheckPeriod = 30000; // BRO-681: Check every 30 seconds
        this.reconnectInProgress = false;
        this.initialized = false;
        this._reconnectResetTimer = null;
        this._probeTimeout = null;
    }

    /**
     * Initialize the connection manager
     */
    init() {
        if (this.initialized) {
            console.debug('🔌 WebSocketConnectionManager: Already initialized');
            return;
        }

        console.debug('🔌 WebSocketConnectionManager: Initializing...');

        this._setupVisibilityListener();
        this._setupFocusListener();
        this._setupPusherStateListener();
        // BRO-1497: the activity-tracking setup that used to be called here is gone.
        // It monkey-patched window.dispatchEvent GLOBALLY so that any event whose
        // type began with the dashboard prefix counted as connection activity, and
        // nothing has dispatched such an event since resources/js/dashboard-realtime.js
        // was deleted on 16 June (8d1d35d1). A global override of a DOM method, for a
        // condition permanently false, is worth removing on its own; leaving it is
        // also how the next person concludes those events must still exist somewhere.
        //
        // NOT a functional loss: the activity timestamp is still updated from four
        // other places (around lines 146, 240, 294 and 323), so staleness detection
        // is unchanged.
        this._setupHealthCheck();
        this._setupSleepDetector();

        this.initialized = true;
        console.debug('🔌 WebSocketConnectionManager: Initialized');
    }

    /**
     * Clean up intervals and listeners
     */
    destroy() {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }
        if (this._reconnectResetTimer) {
            clearTimeout(this._reconnectResetTimer);
            this._reconnectResetTimer = null;
        }
        if (this._probeTimeout) {
            clearTimeout(this._probeTimeout);
            this._probeTimeout = null;
        }
        this.initialized = false;
    }

    /**
     * Set up visibility change listener to detect when tab becomes visible
     */
    _setupVisibilityListener() {
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                this._onTabVisible();
            }
        });
    }

    /**
     * BRO-795: Detect wake from sleep via setInterval time gap.
     * Browsers pause setInterval during sleep, so on wake the elapsed time
     * since the last tick will be much larger than the interval period.
     */
    _setupSleepDetector() {
        const TICK_INTERVAL = 30000;   // Check every 30s
        const SLEEP_THRESHOLD = 60000; // If >60s elapsed between 30s ticks, assume sleep
        let lastTick = Date.now();

        setInterval(() => {
            const now = Date.now();
            const elapsed = now - lastTick;
            lastTick = now;

            if (elapsed > SLEEP_THRESHOLD && !document.hidden) {
                console.debug(`[BRO-795] Sleep/wake detected: ${Math.round(elapsed / 1000)}s gap — triggering refresh`);
                this._onTabVisible();
            }
        }, TICK_INTERVAL);
    }

    /**
     * Set up focus listener as backup for visibility change
     */
    _setupFocusListener() {
        window.addEventListener('focus', () => {
            this._onTabVisible();
        });
    }

    /**
     * Set up Pusher connection state change listeners
     */
    _setupPusherStateListener() {
        // Wait for Echo to be available
        const checkEcho = () => {
            if (window.Echo?.connector?.pusher?.connection) {
                const connection = window.Echo.connector.pusher.connection;

                connection.bind('state_change', (states) => {
                    console.debug('🔌 WebSocketConnectionManager: State changed', states.previous, '->', states.current);
                    this._handleStateChange(states.current, states.previous);
                });

                connection.bind('connected', () => {
                    this._onConnected();
                });

                connection.bind('disconnected', () => {
                    this._onDisconnected();
                });

                connection.bind('error', (error) => {
                    console.debug('🔌 WebSocketConnectionManager: Connection error', error);
                });

                // Track incoming messages as activity proof
                connection.bind('message', () => {
                    this._updateLastActivity();
                });

                // Check initial state
                const currentState = connection.state;
                this.isConnected = currentState === 'connected';
                console.debug('🔌 WebSocketConnectionManager: Initial state:', currentState);

            } else {
                // Retry after a short delay
                setTimeout(checkEcho, 100);
            }
        };

        checkEcho();
    }

    /**
     * BRO-681: Periodic health check to detect silent connection death
     * while the tab is open in the foreground.
     *
     * Runs every 30 seconds. Skips if tab is hidden (handled by visibilitychange).
     * If connection state is not 'connected', triggers reconnect.
     * If connected but stale (no activity for > threshold), probes with ping/pong.
     */
    _setupHealthCheck() {
        this.healthCheckInterval = setInterval(() => {
            // Skip checks while tab is hidden — visibilitychange handler covers that
            if (document.hidden) return;

            const state = this._getConnectionState();
            const timeSinceActivity = Date.now() - this.lastActivity;

            // If Pusher state is NOT connected, trigger reconnect
            if (state !== 'connected' && state !== 'connecting') {
                console.debug('🔌 Health check: Connection lost (state:', state, ')');
                this._dispatchStateChange('reconnecting');
                this.reconnect();
                return;
            }

            // If connected but no activity for > staleThreshold, probe the connection
            if (timeSinceActivity > this.staleThreshold) {
                console.debug('🔌 Health check: Connection stale (' + Math.round(timeSinceActivity / 1000) + 's), probing...');
                this._probeConnection();
            }
        }, this.healthCheckPeriod);
    }

    /**
     * BRO-681: Probe the WebSocket connection by sending a pusher:ping
     * and waiting for a pong response. If no pong within 10 seconds,
     * assume connection is dead and force reconnect.
     */
    _probeConnection() {
        // Don't probe if already reconnecting
        if (this.reconnectInProgress) return;
        // Don't probe if a probe is already in flight
        if (this._probeTimeout) return;

        try {
            const connection = window.Echo?.connector?.pusher?.connection;
            if (!connection) return;

            // Set a timeout — if no pong arrives, connection is dead
            this._probeTimeout = setTimeout(() => {
                this._probeTimeout = null;
                console.debug('🔌 Health check: Ping timeout — forcing reconnect');
                this._dispatchStateChange('reconnecting');
                this.reconnect();
            }, 10000); // 10 second timeout for pong

            // Listen for pong response
            const onPong = () => {
                if (this._probeTimeout) {
                    clearTimeout(this._probeTimeout);
                    this._probeTimeout = null;
                }
                connection.unbind('pong', onPong);
                this._updateLastActivity();
                this.reconnectAttempts = 0; // Connection confirmed alive
                console.debug('✅ Health check: Pong received — connection alive');
            };

            connection.bind('pong', onPong);
            connection.send_event('pusher:ping', {});
        } catch (e) {
            if (this._probeTimeout) {
                clearTimeout(this._probeTimeout);
                this._probeTimeout = null;
            }
            console.debug('🔌 Health check: Probe failed, triggering reconnect', e);
            this._dispatchStateChange('reconnecting');
            this.reconnect();
        }
    }

    /**
     * Handle tab becoming visible
     */
    _onTabVisible() {
        const timeSinceActivity = Date.now() - this.lastActivity;
        const state = this._getConnectionState();

        // BRO-749: Diagnostic — log exact state on tab return
        console.debug('[BRO-749] TAB VISIBLE', {
            connectionState: state,
            timeSinceActivityMs: timeSinceActivity,
            timeSinceActivityHuman: Math.round(timeSinceActivity / 1000) + 's',
            isStale: timeSinceActivity > this.staleThreshold,
            reconnectInProgress: this.reconnectInProgress,
            reconnectAttempts: this.reconnectAttempts,
            echoExists: !!window.Echo,
            pusherState: window.Echo?.connector?.pusher?.connection?.state || 'no-pusher',
            channelCount: Object.keys(window.Echo?.connector?.channels || {}).length,
            channels: Object.keys(window.Echo?.connector?.channels || {}),
        });

        console.debug('🔔 WebSocketConnectionManager: Tab became visible');

        console.debug('🔌 WebSocketConnectionManager: Connection check', {
            state,
            timeSinceActivity: Math.round(timeSinceActivity / 1000) + 's',
            staleThreshold: this.staleThreshold / 1000 + 's'
        });

        // If disconnected or stale, attempt reconnection
        if (state !== 'connected' || timeSinceActivity > this.staleThreshold) {
            console.debug('🔌 WebSocketConnectionManager: Connection stale or lost, attempting recovery...');
            this._dispatchStateChange('reconnecting');
            this.reconnect();
        } else {
            console.debug('✅ WebSocketConnectionManager: Connection appears healthy');
            this._updateLastActivity();
        }

        // BRO-564: Always dispatch tab-visible event so components can refresh data
        // This handles the case where broadcasts were missed while tab was hidden
        // (even if connection stayed alive, browser may have throttled JS execution)
        window.dispatchEvent(new CustomEvent('websocket:tab-visible', {
            detail: {
                timeSinceActivity,
                wasStale: timeSinceActivity > this.staleThreshold
            }
        }));
    }

    /**
     * Handle successful connection
     */
    _onConnected() {
        // BRO-749: Diagnostic — log channel state on reconnection
        console.debug('[BRO-749] CONNECTED', {
            channelCount: Object.keys(window.Echo?.connector?.channels || {}).length,
            channels: Object.keys(window.Echo?.connector?.channels || {}),
            wasReconnecting: this.reconnectInProgress,
            reconnectAttempts: this.reconnectAttempts,
        });
        console.debug('🟢 WebSocketConnectionManager: Connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.reconnectInProgress = false;
        this._updateLastActivity();
        this._dispatchStateChange('connected');

        // Also dispatch echo:connected for backward compatibility
        window.dispatchEvent(new CustomEvent('echo:connected'));

        // BRO-681: Reset reconnect attempt counter after sustained connection
        if (this._reconnectResetTimer) clearTimeout(this._reconnectResetTimer);
        this._reconnectResetTimer = setTimeout(() => {
            this.reconnectAttempts = 0;
        }, 300000); // 5 minutes of stable connection resets the counter
    }

    /**
     * Handle disconnection
     */
    _onDisconnected() {
        // BRO-749: Diagnostic
        console.debug('[BRO-749] DISCONNECTED', {
            tabHidden: document.hidden,
            timeSinceActivity: Math.round((Date.now() - this.lastActivity) / 1000) + 's',
        });
        console.debug('🔴 WebSocketConnectionManager: Disconnected');
        this.isConnected = false;
        this._dispatchStateChange('disconnected');

        // Also dispatch echo:disconnected for backward compatibility
        window.dispatchEvent(new CustomEvent('echo:disconnected'));
    }

    /**
     * Handle state changes
     */
    _handleStateChange(current, previous) {
        if (current === 'connected') {
            this._onConnected();
        } else if (current === 'disconnected' || current === 'failed' || current === 'unavailable') {
            this._onDisconnected();
        } else if (current === 'connecting') {
            this._dispatchStateChange('reconnecting');
        }
    }

    /**
     * Get current Pusher connection state
     */
    _getConnectionState() {
        try {
            if (window.Echo?.connector?.pusher?.connection) {
                return window.Echo.connector.pusher.connection.state;
            }
        } catch (e) {
            console.warn('🔌 WebSocketConnectionManager: Could not get connection state:', e);
        }
        return 'unknown';
    }

    /**
     * Update last activity timestamp
     */
    _updateLastActivity() {
        this.lastActivity = Date.now();
    }

    /**
     * Dispatch connection state change event
     */
    _dispatchStateChange(state) {
        window.dispatchEvent(new CustomEvent('websocket:state-change', {
            detail: { state }
        }));

        // Dispatch specific events for easier listening
        if (state === 'reconnecting') {
            window.dispatchEvent(new CustomEvent('websocket:reconnecting'));
        } else if (state === 'connected') {
            window.dispatchEvent(new CustomEvent('websocket:connected'));
        } else if (state === 'disconnected') {
            window.dispatchEvent(new CustomEvent('websocket:disconnected'));
        } else if (state === 'failed') {
            window.dispatchEvent(new CustomEvent('websocket:failed'));
        }
    }

    /**
     * Attempt to reconnect with exponential backoff
     */
    async reconnect() {
        if (this.reconnectInProgress) {
            console.debug('🔌 WebSocketConnectionManager: Reconnection already in progress');
            return;
        }

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('🔴 WebSocketConnectionManager: Max reconnection attempts reached');
            this._dispatchStateChange('failed');
            this.reconnectInProgress = false;
            return;
        }

        this.reconnectInProgress = true;
        this.reconnectAttempts++;

        // Calculate delay with exponential backoff
        const delay = Math.min(
            this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
            this.maxReconnectDelay
        );

        console.debug(`🔄 WebSocketConnectionManager: Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

        await new Promise(resolve => setTimeout(resolve, delay));

        try {
            const connection = window.Echo?.connector?.pusher?.connection;
            if (connection) {
                // Check if already connected (might have reconnected automatically)
                if (connection.state === 'connected') {
                    console.debug('🟢 WebSocketConnectionManager: Already reconnected');
                    this._onConnected();
                    return;
                }

                // Force reconnection
                console.debug('🔄 WebSocketConnectionManager: Forcing reconnection...');
                connection.connect();
            } else {
                console.warn('🔌 WebSocketConnectionManager: No connection object available');
                this.reconnectInProgress = false;
            }
        } catch (e) {
            console.error('🔌 WebSocketConnectionManager: Reconnection error:', e);
            this.reconnectInProgress = false;

            // Try again if we haven't exceeded max attempts
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnect();
            } else {
                this._dispatchStateChange('failed');
            }
        }
    }

    /**
     * Get current connection state for UI
     */
    getState() {
        if (this.reconnectInProgress) {
            return 'reconnecting';
        }
        return this.isConnected ? 'connected' : 'disconnected';
    }

    /**
     * Reset reconnection attempts (useful after successful operation)
     */
    resetReconnectAttempts() {
        this.reconnectAttempts = 0;
    }
}

// Export singleton instance
const wsConnectionManager = new WebSocketConnectionManager();
export default wsConnectionManager;
